/**
 * R53.27 — Tracciabilità lotto (scansione QR → scheda completa)
 *
 * Pagina mobile-first che mostra la "carta d'identità HACCP" di una produzione:
 * - Dati base + lotto + scadenza
 * - Ingredienti usati (ricevimenti + lotto/scadenza)
 * - Pulizie eseguite il giorno della produzione
 * - Eventuali NC collegate
 *
 * Route: /tracciabilita/:prodId (richiede login)
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError as apiErrMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Hash, Calendar, Flame, ClipboardList, Sparkles,
  AlertTriangle, CheckCircle2, Loader2, FileText, Truck, User,
} from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return s; }
};

const fmtDateTime = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
};

export default function TracciabilitaLotto() {
  const { prodId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/produzioni/${prodId}/tracciabilita`);
        setData(data);
      } catch (e) {
        toast.error(apiErrMsg(e, "Tracciabilità non disponibile"));
      } finally { setLoading(false); }
    })();
  }, [prodId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="tracc-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data || !data.produzione) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 text-center" data-testid="tracc-empty">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto" />
        <p className="text-slate-800 font-bold mt-3">Tracciabilità non disponibile</p>
        <p className="text-[12px] text-slate-500 mt-1">Il lotto scansionato non è stato trovato o non hai i permessi.</p>
        <button onClick={() => navigate(-1)} className="mt-4 inline-block px-4 py-2 rounded-lg bg-[#13294B] text-white text-sm font-bold" data-testid="tracc-back">Torna indietro</button>
      </div>
    );
  }

  const { produzione: p, ricevimenti = [], pulizie_giorno = [], nc_collegate = [], etichette = [] } = data;
  const hasNC = nc_collegate.length > 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="tracc-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 pt-3 pb-5">
        <button onClick={() => navigate(-1)} className="text-white/70 hover:text-white flex items-center gap-1 text-[12px] mb-3" data-testid="tracc-header-back">
          <ArrowLeft className="w-4 h-4" /> Indietro
        </button>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-200 text-[10px] font-bold uppercase tracking-wider">
            <Hash className="w-3 h-3" /> Tracciabilità lotto
          </span>
          {hasNC ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/30 border border-rose-300/50 text-rose-100 text-[10px] font-bold uppercase tracking-wider">
              <AlertTriangle className="w-3 h-3" /> {nc_collegate.length} NC
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/30 border border-emerald-300/50 text-emerald-100 text-[10px] font-bold uppercase tracking-wider">
              <CheckCircle2 className="w-3 h-3" /> OK
            </span>
          )}
        </div>
        <h1 className="text-2xl font-black mt-2 tracking-tight" data-testid="tracc-prodotto">{p.prodotto || "—"}</h1>
        <p className="text-[12px] text-white/70 mt-0.5">Lotto: <span className="font-mono font-bold text-amber-300" data-testid="tracc-lotto">{p.lotto || "—"}</span></p>
      </div>

      {/* Tile 4 dati principali */}
      <div className="px-4 -mt-3 grid grid-cols-2 gap-2">
        <Card icon={Calendar} label="Data preparazione" value={fmtDateTime(p.data)} />
        <Card icon={Flame} label="Processo" value={(p.processi || []).map(x => typeof x === "string" ? x : (x?.tipo || x?.label || "")).filter(Boolean).join(", ") || (p.processo || "—")} />
        <Card icon={User} label="Operatore" value={p.operatore || "—"} />
        <Card icon={ClipboardList} label="Quantità" value={p.quantita != null ? `${p.quantita} ${p.unita || ""}`.trim() : "—"} />
      </div>

      {/* Ingredienti / Ricevimenti */}
      <Section icon={Truck} title="Ingredienti utilizzati" count={ricevimenti.length} testId="tracc-ingredienti">
        {ricevimenti.length === 0 && <Empty>Nessun ingrediente collegato.</Empty>}
        {ricevimenti.map((r) => (
          <Item key={r.id} title={r.prodotto || "—"} subtitle={[r.fornitore, r.lotto && `Lotto ${r.lotto}`].filter(Boolean).join(" · ")} right={r.scadenza ? fmtDate(r.scadenza) : (r.non_deperibile ? "non deperibile" : "—")} />
        ))}
        {/* Ingredienti dettagli (foto / manuali) */}
        {(p.ingredienti_dettagli || []).filter(d => !d.ricevimento_id).map((d, i) => (
          <Item key={`d${i}`} title={d.nome || "—"} subtitle={[d.marca, d.lotto && `Lotto ${d.lotto}`].filter(Boolean).join(" · ")} right={d.scadenza ? fmtDate(d.scadenza) : "—"} />
        ))}
      </Section>

      {/* Pulizie del giorno */}
      <Section icon={Sparkles} title="Pulizie del giorno" count={pulizie_giorno.length} testId="tracc-pulizie">
        {pulizie_giorno.length === 0 && <Empty>Nessuna pulizia registrata in questa data.</Empty>}
        {pulizie_giorno.slice(0, 15).map((pu, i) => (
          <Item key={i} title={`${pu.area || "—"} · ${pu.attivita || ""}`} subtitle={pu.eseguita_da || "—"} right={(pu.eseguita_il || "").slice(11, 16) || "OK"} />
        ))}
        {pulizie_giorno.length > 15 && <p className="text-[10px] text-slate-400 text-center mt-1">… e altre {pulizie_giorno.length - 15}</p>}
      </Section>

      {/* Etichette */}
      {etichette.length > 0 && (
        <Section icon={FileText} title="Etichette emesse" count={etichette.length} testId="tracc-etichette">
          {etichette.slice(0, 5).map((e) => (
            <Item key={e.id} title={e.denominazione || "—"} subtitle={fmtDate(e.data_preparazione)} right={`x${e.numero_copie || 1}`} />
          ))}
        </Section>
      )}

      {/* Non conformità */}
      {hasNC && (
        <Section icon={AlertTriangle} title="Non conformità" count={nc_collegate.length} testId="tracc-nc" tone="bad">
          {nc_collegate.map((nc) => (
            <Item key={nc.id}
                  title={nc.tipo || nc.titolo || "Non conformità"}
                  subtitle={nc.descrizione}
                  right={nc.stato || ""}
                  tone="bad" />
          ))}
        </Section>
      )}

      {/* Footer */}
      <div className="px-4 mt-6 text-center text-[10px] text-slate-400">
        <p>e-Log Ristorazione · {new Date().getFullYear()}</p>
        <p className="mt-0.5">Dati conservati come da art. 14 Reg. UE 178/2002</p>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-3 shadow-sm">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className="mt-1 text-[13px] font-bold text-slate-900 truncate">{value}</p>
    </div>
  );
}

function Section({ icon: Icon, title, count, children, testId, tone }) {
  const headerTone = tone === "bad" ? "text-rose-700" : "text-[#13294B]";
  return (
    <section className="mt-5 px-4" data-testid={testId}>
      <div className={`flex items-center gap-2 mb-2 ${headerTone}`}>
        <Icon className="w-4 h-4" />
        <h2 className="text-[13px] font-bold uppercase tracking-wider">{title}</h2>
        <span className="text-[10px] text-slate-400 ml-1">({count})</span>
      </div>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function Item({ title, subtitle, right, tone }) {
  const ring = tone === "bad" ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border ${ring} p-2.5 flex items-center gap-2`}>
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-bold text-slate-900 truncate">{title}</p>
        {subtitle && <p className="text-[10px] text-slate-500 truncate">{subtitle}</p>}
      </div>
      {right && <p className="text-[10px] font-mono text-slate-600 shrink-0">{right}</p>}
    </div>
  );
}

function Empty({ children }) {
  return <p className="text-[11px] text-slate-400 italic text-center py-3">{children}</p>;
}
